/*
  Chrome Vector — 3D product carousel.

  Cards sit on a real ring in perspective space: each one is positioned with
  sin/cos around the ring and rotated to face outward, so turning the carousel
  is genuine 3D rather than a set of preset "left / centre / right" classes.
  Drag, wheel-free swipe, arrows, dots and keyboard all drive one float value.
*/
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    var track = root.querySelector('[data-cv-carousel-track]');
    var slides = [].slice.call(root.querySelectorAll('[data-cv-carousel-slide]'));
    var dotsWrap = root.querySelector('[data-cv-carousel-dots]');
    var prevBtn = root.querySelector('[data-cv-carousel-prev]');
    var nextBtn = root.querySelector('[data-cv-carousel-next]');
    var live = root.querySelector('[data-cv-carousel-live]');
    if (!track || slides.length === 0) return;

    var n = slides.length;
    var pos = 0;        /* continuous ring position, in card units */
    var target = 0;
    var dragging = false;
    var pointerId = null;
    var lastX = 0, startX = 0, lastT = 0, velocity = 0, dragDist = 0;
    var pressing = false;
    var rafId = 0, animating = false;
    var autoTimer = 0;
    var paused = false;

    var SPREAD = 0.62;      /* radians between neighbouring cards */
    var TILT = 0.92;        /* how much of the ring angle the card takes on */
    var step = 260;         /* px of drag per card, recalculated on resize */
    var radiusX = 300;
    var radiusZ = 210;
    var gapX = 0;

    var CLEARANCE = 1.40;   /* daylight between a card and its neighbour */

    function measure() {
      var w = slides[0].getBoundingClientRect().width || 280;
      var viewport = (track.parentNode && track.parentNode.clientWidth) || window.innerWidth;

      /* A neighbour sits at sin(SPREAD) * radiusX on the ring. That distance
         has to clear half this card plus half of the rotated neighbour --
         which is narrower on screen by cos() -- with room to spare. */
      var neighbourScale = 1 / (1 + 0.14);
      var neighbourHalf = 0.5 * neighbourScale * Math.cos(SPREAD * TILT) * w;
      var needed = (0.5 * w + neighbourHalf) * CLEARANCE;
      radiusX = needed / Math.sin(SPREAD);

      /* But never push the neighbour so far that it leaves the frame -- on a
         narrow screen it should still peek in at the edges. */
      var capFactor = viewport < 700 ? 0.54 : 0.48;
      var maxRadius = (viewport * capFactor) / Math.sin(SPREAD);
      radiusX = Math.min(radiusX, maxRadius);

      radiusZ = Math.min(w * 0.55, 240);
      gapX = radiusX;
      step = Math.max(160, Math.sin(SPREAD) * radiusX);
    }

    /* Shortest signed distance from card i to the current position. */
    function delta(i) {
      var d = (i - pos) % n;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      return d;
    }

    /* Only transform and opacity are written every frame -- both are composited
       on the GPU. Everything else (filter, z-index, classes, ARIA) forces style
       or paint work, so it is written only when the value actually changes. */
    function render() {
      for (var i = 0; i < n; i++) {
        var slide = slides[i];
        var cache = slide._cvState || (slide._cvState = {});
        var d = delta(i);
        var ad = Math.abs(d);

        var ang = d * SPREAD;
        var x = Math.sin(ang) * radiusX;
        var z = (Math.cos(ang) - 1) * radiusZ;
        var scale = 1 / (1 + ad * 0.14);
        var lift = ad * 8;
        var fade = 1 - Math.min(1, Math.max(0, (ad - 1.7) / 0.8));

        slide.style.transform =
          'translate3d(' + x.toFixed(1) + 'px,' + lift.toFixed(1) + 'px,' + z.toFixed(1) + 'px)' +
          ' rotateY(' + (ang * TILT * 180 / Math.PI).toFixed(2) + 'deg)' +
          ' scale(' + scale.toFixed(3) + ')';
        slide.style.opacity = fade.toFixed(3);

        var hidden = (n > 2 && ad > n / 2 - 0.001);
        if (cache.hidden !== hidden) {
          cache.hidden = hidden;
          slide.style.visibility = hidden ? 'hidden' : '';
        }

        var zi = 200 - Math.round(ad * 20);
        if (cache.zi !== zi) { cache.zi = zi; slide.style.zIndex = String(zi); }

        var active = ad < 0.5;
        if (cache.active !== active) {
          cache.active = active;
          slide.classList.toggle('is-active', active);
          /* Only the front card takes a tap straight through to the product. */
          slide.tabIndex = active ? 0 : -1;
        }

        var buried = ad > 1.6;
        if (cache.buried !== buried) {
          cache.buried = buried;
          slide.setAttribute('aria-hidden', buried ? 'true' : 'false');
        }
      }
      updateDots();
    }

    function activeIndex() {
      return ((Math.round(pos) % n) + n) % n;
    }

    var lastDot = -1;
    function updateDots() {
      if (!dotsWrap) return;
      var active = activeIndex();
      if (active === lastDot) return;
      lastDot = active;
      var kids = dotsWrap.children;
      for (var i = 0; i < kids.length; i++) {
        var on = i === active;
        kids[i].classList.toggle('is-active', on);
        kids[i].setAttribute('aria-current', on ? 'true' : 'false');
      }
    }

    function normalise() {
      /* Keep the float small so precision never drifts on long sessions. */
      while (pos > n) { pos -= n; target -= n; }
      while (pos < -n) { pos += n; target += n; }
    }

    function tick() {
      var diff = target - pos;
      pos += diff * 0.14;
      if (Math.abs(diff) < 0.0008) { pos = target; animating = false; }
      normalise();
      render();
      if (animating || dragging) rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (animating || dragging) return;
      animating = true;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(tick);
    }

    function goTo(index, fromUser) {
      var base = Math.round(target);
      var d = (index - ((base % n) + n) % n) % n;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      target = base + d;
      if (reduced) { pos = target; render(); }
      else start();
      if (fromUser) restartAuto();
      announce();
    }

    function nudge(dir) {
      target = Math.round(target) + dir;
      if (reduced) { pos = target; render(); }
      else start();
      restartAuto();
      announce();
    }

    function announce() {
      if (!live) return;
      var slide = slides[activeIndex()];
      var name = slide.getAttribute('data-cv-carousel-title') || '';
      live.textContent = name ? name + ' — ' + (activeIndex() + 1) + ' of ' + n : '';
    }

    /* ---- dots ---- */
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cv-carousel__dot';
        dot.setAttribute('aria-label', slide.getAttribute('data-cv-carousel-title') || ('Product ' + (i + 1)));
        dot.addEventListener('click', function () { goTo(i, true); });
        dotsWrap.appendChild(dot);
      });
    }

    /* ---- drag ---- */
    var DRAG_THRESHOLD = 6;   /* px of travel before this counts as a drag */

    function onDown(e) {
      if (e.button != null && e.button !== 0) return;
      /* Deliberately NOT capturing the pointer yet. Capturing on pointerdown
         retargets the compatibility click to the track, so the click never
         reaches the product link and cards cannot be opened. Capture is taken
         only once this turns out to be a real drag. */
      pressing = true;
      dragging = false;
      dragDist = 0;
      velocity = 0;
      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      lastT = performance.now();
      stopAuto();
    }

    function onMove(e) {
      if (!pressing || (pointerId !== null && e.pointerId !== pointerId)) return;
      var now = performance.now();
      var dx = e.clientX - lastX;
      var dt = Math.max(1, now - lastT);
      dragDist = Math.abs(e.clientX - startX);

      if (!dragging) {
        if (dragDist < DRAG_THRESHOLD) { lastT = now; return; }
        /* Now it is a drag: take the pointer so it keeps tracking outside
           the element, and start the render loop. */
        dragging = true;
        root.classList.add('is-dragging');
        if (track.setPointerCapture) { try { track.setPointerCapture(e.pointerId); } catch (err) {} }
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(tick);
      }

      pos -= dx / step;
      target = pos;
      velocity = (-dx / step) / dt * 16;
      lastX = e.clientX;
      lastT = now;
      normalise();
      render();
    }

    function onUp(e) {
      if (!pressing) return;
      pressing = false;
      pointerId = null;
      if (!dragging) { restartAuto(); return; }   /* a tap: let the click run */
      dragging = false;
      root.classList.remove('is-dragging');
      /* Throw the ring, then settle onto the nearest card. */
      var projected = pos + velocity * 7;
      target = Math.round(projected);
      start();
      restartAuto();
      announce();
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* A tap on a side card brings it forward instead of opening it. */
    slides.forEach(function (slide, i) {
      slide.addEventListener('click', function (e) {
        if (dragDist > 8) { e.preventDefault(); dragDist = 0; return; }
        if (Math.abs(delta(i)) > 0.4) { e.preventDefault(); goTo(i, true); }
      });
      slide.addEventListener('focus', function () {
        if (Math.abs(delta(i)) > 0.4) goTo(i, true);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { nudge(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { nudge(1); });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
    });

    /* ---- autoplay ---- */
    function stopAuto() { window.clearInterval(autoTimer); autoTimer = 0; }
    function restartAuto() {
      stopAuto();
      if (reduced || paused || n < 2) return;
      autoTimer = window.setInterval(function () {
        if (document.hidden) return;
        target = Math.round(target) + 1;
        start();
      }, 6200);
    }

    root.addEventListener('mouseenter', function () { paused = true; stopAuto(); });
    root.addEventListener('mouseleave', function () { paused = false; restartAuto(); });
    root.addEventListener('focusin', function () { paused = true; stopAuto(); });
    root.addEventListener('focusout', function () {
      if (!root.contains(document.activeElement)) { paused = false; restartAuto(); }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAuto(); else restartAuto();
    });

    window.addEventListener('resize', function () { measure(); render(); }, { passive: true });

    measure();
    root.classList.add('is-ready');
    target = 0; pos = 0;
    render();
    announce();

    /* Only spin on its own once it is actually on screen. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { restartAuto(); } else { stopAuto(); }
        });
      }, { threshold: 0.25 });
      io.observe(root);
    } else {
      restartAuto();
    }
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-cv-carousel]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init when the section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-cv-carousel]');
    if (el) init(el);
  });
})();
